import {Injectable, Injector, OnDestroy, Type} from "@angular/core";
import {State} from "./state";
import {
  from,
  isObservable,
  lastValueFrom,
  Observable,
  of,
  ReplaySubject,
  Subject,
  switchMap,
  take,
  takeUntil
} from "rxjs";
import {isPromise} from "rxjs/internal/util/isPromise";
import {Environment} from "../../util/environment";

export type ForgeRockProfile = 'loginSecurity' | 'contactInfo';
export type CallbackValueResolver = string | ((values: Record<string, unknown>) => unknown);

export interface ForgeRockJourney {
  updateProfile: ForgeRockProfile;
  exitRoute: string[];
  onExit?: () => void;
}

export interface OtpPopupData {
  originalEmailId: string;
  newEmailId: string;
  isEmailAsUserId: boolean;
  source: 'login' | 'contact';
}

interface ForgeRockSubmission {
  values: Record<string, unknown>;
  callbackMap: Record<string, CallbackValueResolver>;
}

/**
 * Stores a reference to the current state and delegates to it for all state-specific work
 * ({@link start} & {@link stop}). The context communicates with the states via the {@link State} interface. The context
 * exposes a setter ({@link changeState}) for passing it a new state.
 *
 * @summary
 *
 * Why were Promises used instead of Observables?  Promises are asynchronous but require no special invocation such as
 * the `subscribe` of Observables, which is useful when you want to execute an async operation but don't require the
 * response.
 *
 * @example
 * // Promise
 * this.stateContext.changeState(InitState);
 *
 * // instead of Observable
 * this.stateContext.changeState(InitState).subscribe();
 *
 * @see {@link State}, {@link https://refactoring.guru/design-patterns/state|State Pattern}
 */
@Injectable({
  providedIn: 'root'
})
export class StateContext implements OnDestroy {
  private _done = new Subject<void>();
  private journey?: ForgeRockJourney;
  private submission?: ForgeRockSubmission;
  private otpPopupData?: OtpPopupData;

  /**
   * Current state of this context, can start null, but should never transition to null/undefined
   */
  private _currentState: State | null = null;
  private _onChange = new ReplaySubject<State | null>(1);

  constructor(
    private injector: Injector
  ) {
  }

  ngOnDestroy(): void {
    this._done.next(void 0);
    this._done.complete();
  }

  stateChanges(): Observable<State> {
    return this._onChange.asObservable().pipe(takeUntil<State | null>(this._done));
  }

  beginJourney(journey: ForgeRockJourney): void {
    this.journey = journey;
    this.submission = undefined;
    this.otpPopupData = undefined;
  }

  getJourney(): ForgeRockJourney {
    if (!this.journey) throw new Error('ForgeRock journey has not been configured');
    return this.journey;
  }

  setSubmission(
    values: Record<string, unknown>,
    callbackMap: Record<string, CallbackValueResolver> = {}
  ): void {
    this.submission = { values, callbackMap };
  }

  getSubmission(): ForgeRockSubmission {
    if (!this.submission) throw new Error('ForgeRock submission has not been configured');
    return this.submission;
  }

  setOtpPopupData(data: OtpPopupData): void {
    this.otpPopupData = data;
  }

  getOtpPopupData(): OtpPopupData {
    if (!this.otpPopupData) throw new Error('OTP popup data has not been configured');
    return this.otpPopupData;
  }

  hasActiveJourney(updateProfile?: ForgeRockProfile): boolean {
    return !!this.journey && (!updateProfile || this.journey.updateProfile === updateProfile);
  }

  clearJourney(): void {
    this.journey = undefined;
    this.submission = undefined;
    this.otpPopupData = undefined;
  }

  /**
   * Changes this context to the new state.  This operation executes asynchronously and can be waited.  If there is a
   * current state, it is first {@link stop}ped, then the new state is {@link start}ed.
   *
   * @example
   * // invoke and forget - still invoked async
   * this.stateContext.changeState(MyState);
   *
   * // invoke and continue once complete
   * this.stateContext.changeState(MyState).then(() => ... proceed ...);
   *
   * @param state - to assign to this context
   * @return completion of the state change
   */
  changeState(state: Type<State>): Promise<void> {
    const newState: State = this.injector.get(state);
    return lastValueFrom(this.stop().pipe(switchMap(() => this.start(newState))));
  }

  /**
   * Replay the current state, if any.  The current state is first {@link stop}ped, and then it is re-{@link start}ed.
   *
   * @throws reject if no state exists to replay
   */
  replay(): Promise<void> {
    if (this._currentState) return lastValueFrom(this.stop().pipe(switchMap(() => this.start(this._currentState))));
    else return Promise.reject(`no current state to replay!`);
  }

  /**
   * Stop the current state, if any, asynchronously.  Ensure stop ALWAYS completes, or state changes will get hung up.
   *
   * @return completion of the stop operation
   */
  private stop(): Observable<void> {
    if (this._currentState) {
      const stop = this._currentState.stop?.();
      if (isObservable(stop) || isPromise(stop)) return from(stop).pipe(take(1));
      else return of(stop);
    }
    return of(void 0);
  }

  /**
   * Start the new state asynchronously and assigns it to the current state. Ensure stop ALWAYS completes, or state
   * changes will get hung up.
   *
   * @param state - to be started
   * @returns completion of the start operation
   */
  private start(state: State): Observable<void> {
    this._onChange.next((this._currentState = state));

    const start = this._currentState.start();
    if (isObservable(start) || isPromise(start)) return from(start).pipe(take(1))
    else return of(start);
  }

  /**
   * Logs state changes to the console
   */
  initStateChangeLogging(): void {
    if (!Environment.isProduction()) {
      this.stateChanges()
        .pipe(takeUntil(this._done))
        .subscribe((state) => {
          console.debug(`🔑 STATE:${state?.name}`);
        });
    }
  }
}
